"use client";
import { useEffect, useState } from "react";
import { useT } from "@/lib/i18n";
import { useApi } from "@/lib/hooks/use-api";
import { useAuth } from "@/lib/auth/provider";
import { post } from "@/lib/api/client";
import {
  Badge,
  Button,
  DataState,
  Empty,
  Modal,
  PageHeading,
  Stat,
} from "@/components/ui";
import {
  Crosshair,
  ExternalLink,
  Eye,
  Flag,
  Loader2,
  LockKeyhole,
  MonitorPlay,
  RefreshCw,
  Swords,
  Terminal,
  Timer,
  Trophy,
  User,
} from "lucide-react";
import Link from "next/link";
import { RequireAuth } from "@/components/auth";
import { ArenaEntry } from "@/components/arena/entry";

interface MatchItem {
  id: string;
  scenario: string;
  mission?: string | null;
  red_user_id: string;
  blue_user_id: string;
  status: string;
  phase_minutes_left: number | null;
  lab_run_id: string | null;
  red_score: number;
  blue_score: number;
  red_name: string;
  blue_name: string;
  red_workspace_url?: string;
  blue_workspace_url?: string;
  red_terminal_url?: string;
  blue_terminal_url?: string;
  target_ip?: string;
  red_flag_status: string | null;
  red_vuln_status: string | null;
  red_report_status: string | null;
  blue_detection_status: string | null;
  blue_report_status: string | null;
  blue_uptime_pct: number | null;
  blue_fixed: number | null;
  created_at: string;
  finished_at: string | null;
}

interface TournamentListItem {
  id: string;
  name: string;
  status: string;
  created_at: string;
  finished_at: string | null;
  winner_side: string | null;
  red_total: number;
  blue_total: number;
  red_members: number;
  blue_members: number;
}

interface TournamentMemberView {
  id: string;
  name: string;
  side: string;
  matches: number;
  points: number;
  active_match: boolean;
}

interface TournamentDetail {
  id: string;
  name: string;
  scenario: string;
  status: string;
  created_at: string;
  finished_at: string | null;
  winner_side: string | null;
  red_total: number;
  blue_total: number;
  red_members: TournamentMemberView[];
  blue_members: TournamentMemberView[];
  matches: MatchItem[];
}


export function ArenaPage() {
  const t = useT();
  const auth = useAuth();
  const isStaff =
    auth.user?.role === "ADMIN" || auth.user?.role === "TEACHER";
  const matches = useApi<{ items: MatchItem[] }>(
    auth.authenticated ? "/arena" : null,
    10_000,
  );
  const [createOpen, setCreateOpen] = useState(false);
  const [side, setSide] = useState<"red" | "blue" | null>(null);
  const [entered, setEntered] = useState(false);

  const mine = matches.data?.items ?? [];

  // Арена ашылғанда бүткіл сайт қара боп кетеді
  useEffect(() => {
    document.documentElement.classList.add("site-dark");
    return () => document.documentElement.classList.remove("site-dark");
  }, []);

  // Командалық таңдау — браузерде сақталады (қайта кіргенде сол болады)
  useEffect(() => {
    try {
      const s = localStorage.getItem("cyberalem.arena.side");
      if (s === "red" || s === "blue") {
        setSide(s);
        setEntered(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  function enterArena(s: "red" | "blue") {
    setSide(s);
    setEntered(true);
    try {
      localStorage.setItem("cyberalem.arena.side", s);
    } catch {
      /* ignore */
    }
  }

  function resetSide() {
    setEntered(false);
    setSide(null);
    try {
      localStorage.removeItem("cyberalem.arena.side");
    } catch {
      /* ignore */
    }
  }

  // Maintenance mode: арена жабық (503) — ешкім кіре алмайды
  const arenaClosed = (matches.error?.message ?? "").includes("Арена уақытша жабық");

  if (arenaClosed) {
    return (
      <div className="page arena-dark">
        <div className="arena-closed" role="alert">
          <div className="arena-closed-scan" aria-hidden />
          <div className="arena-closed-icon">
            <LockKeyhole size={34} strokeWidth={1.4} />
          </div>
          <h1 className="arena-closed-title">{t("arena.closed_title")}</h1>
          <p className="muted">{t("arena.closed_text")}</p>
          <div className="arena-closed-rows" aria-hidden>
            <span>SYSTEM // ARENA_NODE</span>
            <span>STATUS // MAINTENANCE</span>
            <span>ACCESS // DENIED</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page arena-dark">
      {!entered && <ArenaEntry onEnter={enterArena} />}
      <PageHeading
        eyebrow={t("arena.page_eyebrow")}
        title={t("arena.page_title")}
        description={t("arena.page_desc")}
      />
      {side && (
        <div className="inline" style={{ marginBottom: 10, gap: 8 }}>
          <span className={`side-badge side-${side}`}>
            {side === "red" ? t("arena.side_red") : t("arena.side_blue")}
          </span>
          <button className="linklike" onClick={resetSide} type="button">
            қайта таңдау
          </button>
        </div>
      )}
      <div
        className="inline"
        style={{
          justifyContent: "space-between",
          marginBottom: "var(--space-4, 14px)",
        }}
      >
        <span className="muted">{t("arena.match_flow")}</span>
        {isStaff && (
          <Button onClick={() => setCreateOpen(true)}>
            <Swords size={16} /> {t("arena.new_match")}
          </Button>
        )}
      </div>
      <TournamentBlock onChanged={matches.reload} />
      {!auth.authenticated ? (
        <Empty
          title={t("arena.guest_title")}
          text={t("arena.guest_text")}
          action={
            <Link className="button primary" href="/login">
              {t("auth.login_btn")}
            </Link>
          }
        />
      ) : (
      <DataState
        loading={matches.loading}
        error={matches.error}
        retry={matches.reload}
      >
        {mine.length === 0 ? (
          <Empty
            title={t("arena.no_matches_title")}
            text={
              isStaff
                ? t("arena.no_matches_staff")
                : t("arena.no_matches_user")
            }
          />
        ) : (
        <div className="stack">
          {mine.map((m) => (
            <MatchCard
              key={m.id}
              match={m}
              isStaff={!!isStaff}
              myRole={
                auth.user && auth.user.id === m.red_user_id
                  ? "red"
                  : auth.user && auth.user.id === m.blue_user_id
                    ? "blue"
                    : undefined
              }
              onChanged={matches.reload}
            />
          ))}
        </div>
        )}
      </DataState>
      )}
      {isStaff && (
        <CreateMatchModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={matches.reload}
        />
      )}
    </div>
  );
}


/* ------------------------- Командалық турнир ------------------------- */

function TournamentBlock({ onChanged }: { onChanged: () => void }) {
  const t = useT();
  const auth = useAuth();
  const isStaff =
    auth.user?.role === "ADMIN" || auth.user?.role === "TEACHER";
  const list = useApi<{ items: TournamentListItem[] }>(
    auth.authenticated ? "/arena/tournaments" : null,
    10_000,
  );
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState<"" | "create" | "join-red" | "join-blue" | "leave" | "round" | "finish">("");
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  const detail = useApi<TournamentDetail>(
    selected ? `/arena/tournaments/${selected}` : null,
    10_000,
  );
  const detailData = detail.data;

  async function act(kind: typeof busy, fn: () => Promise<unknown>) {
    setBusy(kind);
    setMsg(undefined);
    try {
      await fn();
      await Promise.all([list.reload(), detail.reload(), onChanged()]);
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy("");
    }
  }

  async function createTournament() {
    if (!name.trim()) return;
    await act("create", async () => {
      const res = await post<{ id: string }>("/arena/tournaments", { name: name.trim() });
      setName("");
      setSelected(res.id);
    });
  }

  const mySide = detailData
    ? [...detailData.red_members, ...detailData.blue_members].find((m) => m.id === auth.user?.id)?.side
    : undefined;
  const canRound = isStaff || mySide !== undefined;

  return (
    <div className="panel" style={{ marginBottom: 22 }}>
      <div className="between" style={{ marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 17 }}>
            <Trophy size={17} style={{ verticalAlign: -3, color: "var(--accent)" }} /> {t("arena.tournaments_title")}
          </h2>
          <p className="muted" style={{ margin: "4px 0 0", fontSize: 13 }}>
            {t("arena.tournaments_desc")}
          </p>
        </div>
        {isStaff && (
          <div style={{ display: "flex", gap: 8 }}>
            <input
              style={{ width: 230 }}
              placeholder={t("arena.tournament_name_ph")}
              value={name}
              maxLength={80}
              onChange={(e) => setName(e.target.value)}
            />
            <Button busy={busy === "create"} onClick={createTournament} disabled={!name.trim()}>
              {t("arena.tournament_create")}
            </Button>
          </div>
        )}
      </div>

      {!auth.authenticated ? (
        <Empty
          title={t("arena.guest_title")}
          text={t("arena.guest_text")}
          action={
            <Link className="button primary" href="/login">
              {t("auth.login_btn")}
            </Link>
          }
        />
      ) : (
      <DataState loading={list.loading} error={list.error} retry={list.reload}>
        {(list.data?.items ?? []).length === 0 ? (
          <Empty title={t("arena.tournament_none")} text={isStaff ? t("arena.tournament_none_staff") : t("arena.tournament_none_user")} />
        ) : (
          <div className="tourney-grid">
            {(list.data?.items ?? []).map((x) => (
              <button
                key={x.id}
                type="button"
                onClick={() => setSelected(x.id === selected ? null : x.id)}
                className="tourney-chip"
                data-active={x.id === selected ? "true" : "false"}
              >
                <span className="tourney-name">{x.name}</span>
                <span className="tourney-meta">
                  <span className="tourney-chip-status" data-status={x.status}>
                    {t(`arena.tournament_status_${x.status.toLowerCase()}`)}
                  </span>
                  <strong>
                    <span className="score-red">{x.red_total}</span> : <span className="score-blue">{x.blue_total}</span>
                  </strong>
                  <span className="muted">
                    {x.red_members} vs {x.blue_members} {t("arena.tournament_players")}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </DataState>
      )}

      {selected && detailData && selected === detailData.id && (
        <div className="tourney-detail" style={{ marginTop: 16 }}>
          <div className="between" style={{ marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {detailData.name}
              {detailData.winner_side && (
                <span className="tourney-winner" style={{ marginLeft: 8 }}>
                  {t("arena.tournament_winner")}: {detailData.winner_side === "RED" ? t("arena.team_red") : t("arena.team_blue")}
                </span>
              )}
              {detailData.winner_side === null && detailData.status === "FINISHED" && (
                <span className="tourney-winner tie" style={{ marginLeft: 8 }}>{t("arena.tournament_tie")}</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {detailData.status !== "FINISHED" && (
                <>
                  {!mySide && (
                    <>
                      <Button variant="ghost" busy={busy === "join-red"} onClick={() => act("join-red", () => post(`/arena/tournaments/${detailData.id}/join`, { side: "RED" }))}>
                        {t("arena.tournament_join_red")}
                      </Button>
                      <Button variant="ghost" busy={busy === "join-blue"} onClick={() => act("join-blue", () => post(`/arena/tournaments/${detailData.id}/join`, { side: "BLUE" }))}>
                        {t("arena.tournament_join_blue")}
                      </Button>
                    </>
                  )}
                  {mySide && (
                    <Button variant="ghost" busy={busy === "leave"} onClick={() => act("leave", () => post(`/arena/tournaments/${detailData.id}/leave`))}>
                      {t("arena.tournament_leave")}
                    </Button>
                  )}
                  {canRound && (
                    <Button busy={busy === "round"} onClick={() => act("round", () => post(`/arena/tournaments/${detailData.id}/round`))}>
                      <Swords size={15} /> {t("arena.tournament_start_round")}
                    </Button>
                  )}
                </>
              )}
              {isStaff && detailData.status !== "FINISHED" && (
                <Button variant="danger" busy={busy === "finish"} onClick={() => act("finish", () => post(`/arena/tournaments/${detailData.id}/finish`))}>
                  {t("arena.tournament_finish")}
                </Button>
              )}
            </div>
          </div>
          {msg && <p className={msg.ok ? "muted" : "form-error"} style={{ fontSize: 13 }}>{msg.text}</p>}
          <div className="tourney-teams">
            <TeamColumn
              title={t("arena.team_red")}
              color="#c0392b"
              total={detailData.red_total}
              members={detailData.red_members}
              winner={detailData.status === "FINISHED" && detailData.winner_side === "RED"}
            />
            <div className="tourney-score" aria-hidden>
              <span>{detailData.red_total}</span>
              <span className="colon">:</span>
              <span>{detailData.blue_total}</span>
            </div>
            <TeamColumn
              title={t("arena.team_blue")}
              color="#2563eb"
              total={detailData.blue_total}
              members={detailData.blue_members}
              winner={detailData.status === "FINISHED" && detailData.winner_side === "BLUE"}
            />
          </div>
          {detailData.matches.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <p className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
                {t("arena.tournament_rounds")}: {detailData.matches.length}
              </p>
              <div className="tourney-matches">
                {detailData.matches.map((m, i) => (
                  <div key={m.id} className="tourney-match-row">
                    <span className="muted">#{i + 1}</span>
                    <strong style={{ color: "#c0392b" }}>{m.red_name}</strong>
                    <span className="muted">vs</span>
                    <strong style={{ color: "#2563eb" }}>{m.blue_name}</strong>
                    <span className="tourney-match-status">{t(`arena.phase_${m.status}`) === `arena.phase_${m.status}` ? m.status : t(`arena.phase_${m.status}`)}</span>
                    <strong>{m.red_score}:{m.blue_score}</strong>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TeamColumn({
  title,
  color,
  total,
  members,
  winner,
}: {
  title: string;
  color: string;
  total: number;
  members: TournamentMemberView[];
  winner: boolean;
}) {
  const t = useT();
  return (
    <div className="tourney-team" style={{ borderTop: `3px solid ${color}` }}>
      <div className="between" style={{ marginBottom: 8 }}>
        <strong style={{ color }}>{title}</strong>
        {winner && <Trophy size={16} style={{ color }} />}
      </div>
      {members.length === 0 ? (
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>{t("arena.tournament_no_members")}</p>
      ) : (
        <ul className="tourney-players" style={{ margin: 0, paddingLeft: 18 }}>
          {members.map((m) => (
            <li key={m.id} style={{ fontSize: 13, padding: "3px 0" }}>
              {m.name}
              <span className="muted" style={{ marginLeft: 8, fontSize: 12 }}>
                {m.matches} {t("arena.tournament_matches_of")} · {m.points} {t("arena.tournament_points")}
              </span>
              {m.active_match && <span className="tourney-in-match" style={{ marginLeft: 6 }}>{t("arena.tournament_in_match")}</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>
        {t("arena.tournament_team_points")}: <strong style={{ color }}>{total}</strong>
      </p>
    </div>
  );
}

/* ------------------------- Желі топологиясы ------------------------- */

function TopologyDiagram({
  targetIp,
  targetName,
  redUrl,
  blueUrl,
  redName,
  blueName,
}: {
  targetIp?: string;
  targetName?: string | null;
  redUrl?: string;
  blueUrl?: string;
  redName: string;
  blueName: string;
}) {
  const subnet = targetIp
    ? `${targetIp.split(".").slice(0, 3).join(".")}.0/24`
    : "10.x.x.0/24";
  const node = (
    x: number,
    y: number,
    color: string,
    title: string,
    sub: string,
    link?: string,
  ) => (
    <g>
      <rect x={x - 88} y={y - 30} width={176} height={60} rx={10}
        fill={color + "18"} stroke={color} strokeWidth={1.5} />
      <text x={x} y={y - 8} textAnchor="middle" fontSize={13} fontWeight={700} fill={color}>
        {title}
      </text>
      <text x={x} y={y + 10} textAnchor="middle" fontSize={11} fill="currentColor" opacity={0.85}>
        {sub}
      </text>
      {link && (
        <a href={link} target="_blank" rel="noreferrer">
          <text x={x} y={y + 24} textAnchor="middle" fontSize={10} fill="#4f8cff" textDecoration="underline">
            GUI desktop →
          </text>
        </a>
      )}
    </g>
  );
  const line = (x1: number, y1: number, x2: number, y2: number) => (
    <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="currentColor" strokeOpacity={0.35} strokeWidth={1.5} />
  );
  return (
    <div className="arena-topology card" style={{ padding: 12 }}>
      <div className="inline" style={{ marginBottom: 4 }}>
        <span className="muted small">Желі топологиясы — оқу сегменті: {subnet} (раутерден ешкім сыртына шыға алмайды)</span>
      </div>
      <svg viewBox="0 0 640 210" width="100%" height={200} role="img" aria-label="Network topology">
        {line(320, 62, 120, 130)}
        {line(320, 62, 320, 130)}
        {line(320, 62, 520, 130)}
        {/* Router */}
        <g>
          <rect x={244} y={14} width={152} height={48} rx={10} fill="currentColor" fillOpacity={0.08} stroke="currentColor" strokeOpacity={0.5} strokeWidth={1.5} />
          <text x={320} y={35} textAnchor="middle" fontSize={13} fontWeight={700}>📡 Router (egress NAT)</text>
          <text x={320} y={52} textAnchor="middle" fontSize={10} opacity={0.7}>тек Kali workload-ына internet</text>
        </g>
        {node(120, 160, "#e5484d", `RED Kali — ${redName}`, "workstation · шабуыл", redUrl)}
        {node(320, 160, "#e8a13a", targetName ? targetName.slice(0, 26) : "Target — Mini-Server", (targetIp ?? "IP…") + " · web:8080 ssh:22", undefined)}
        {node(520, 160, "#3b82f6", `BLUE Kali — ${blueName}`, "workstation · қорғаныс", blueUrl)}
      </svg>
    </div>
  );
}

/* ------------------------------ Карта ------------------------------ */

function MatchCard({
  match: m,
  isStaff,
  myRole,
  onChanged,
}: {
  match: MatchItem;
  isStaff: boolean;
  myRole: "red" | "blue" | undefined;
  onChanged: () => void;
}) {
  const active = ["PREPARING", "PREP", "LIVE", "REPORTS"].includes(m.status);
  const [frameFailed, setFrameFailed] = useState(false);
  const workspaceUrl =
    isStaff && m.red_workspace_url
      ? m.red_workspace_url
      : myRole === "red"
        ? m.red_workspace_url
        : myRole === "blue"
          ? m.blue_workspace_url
          : undefined;
  const terminalUrl =
    isStaff && m.red_terminal_url
      ? m.red_terminal_url
      : myRole === "red"
        ? m.red_terminal_url
        : myRole === "blue"
          ? m.blue_terminal_url
          : undefined;

  return (
    <div className="card arena-card">
      <div
        className="inline"
        style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}
      >
        <div className="inline">
          <Badge value={m.status} />
          <span className="muted">{m.scenario}</span>
        </div>
        {active && m.phase_minutes_left != null && (
          <span className="arena-timer inline">
            <Timer size={15} /> {m.phase_minutes_left} мин қалды
          </span>
        )}
      </div>

      <div className="arena-teams">
        <TeamChip label="RED" name={m.red_name} score={m.red_score} color="red" mine={myRole === "red"} />
        <span className="arena-vs">VS</span>
        <TeamChip label="BLUE" name={m.blue_name} score={m.blue_score} color="blue" mine={myRole === "blue"} />
      </div>

      {/* Миссия: Red = басып алу (hacker фон), Blue = қорғау (NOC фон) */}
      {active && (
        <div className="arena-mission">
          <div className="mission-side mission-red">
            <div className="mission-kicker">RED · ШАБУЫЛ</div>
            <h3>МИССИЯ: БАСЫП АЛУ</h3>
            <p className="mission-target">{m.mission ?? "Нысана сервері"}</p>
            <p className="mission-text">
              {m.mission ?? "Нысана"} серверіне еніп, flag-ті алып шық. Жүйені
              тоқтатпа — Blue бақылап тұр.
            </p>
          </div>
          <div className="mission-vs">VS</div>
          <div className="mission-side mission-blue">
            <div className="mission-kicker">BLUE · ҚОРҒАНЫС</div>
            <h3>МИССИЯ: ҚОРҒАУ</h3>
            <p className="mission-target">{m.mission ?? "Нысана сервері"}</p>
            <p className="mission-text">
              {m.mission ?? "Нысана"} серверін шабуылдан қорға: қызмет
              қолжетімді болсын, осалдықты жап, шабуылшыны анықта.
            </p>
          </div>
        </div>
      )}

      {/* Желі топологиясы: раутер + Red + Blue + Target */}
      {active && (
        <TopologyDiagram
          targetIp={m.target_ip}
          targetName={m.mission}
          redUrl={isStaff || myRole === "red" ? m.red_workspace_url : undefined}
          blueUrl={isStaff || myRole === "blue" ? m.blue_workspace_url : undefined}
          redName={m.red_name}
          blueName={m.blue_name}
        />
      )}

      {/* Мақсаттар */}
      <div className="arena-goals">
        <div className="arena-goal-block">
          <h4 className="red-t">Red мақсаттары</h4>
          <GoalRow ok={m.red_flag_status === "PASSED"} pend={m.red_flag_status === "PENDING"} label="G1: Flag-ті алу (60%)" />
          <GoalRow ok={m.red_vuln_status === "PASSED"} pend={m.red_vuln_status === "PENDING"} label="G2: Осалдықты сипаттау (20%)" />
          <GoalRow ok={m.red_report_status === "PASSED"} pend={m.red_report_status === "PENDING"} label="G3: Ресми есеп (20%)" />
        </div>
        <div className="arena-goal-block">
          <h4 className="blue-t">Blue мақсаттары</h4>
          <GoalRow ok={m.blue_uptime_pct != null && m.blue_uptime_pct >= 99} label={`B1: Сервис қолжетімділігі (40%)${m.blue_uptime_pct != null ? ` — ${m.blue_uptime_pct}%` : ""}`} />
          <GoalRow ok={m.blue_fixed === 1} label="B2: Осалдықты түзету (30%)" />
          <GoalRow ok={m.blue_detection_status === "PASSED"} pend={!!m.blue_detection_status && m.blue_detection_status !== "PASSED"} label="B3: Оқиғаны анықтау — Red IP (20%)" />
          <GoalRow ok={m.blue_report_status === "PASSED"} pend={m.blue_report_status === "PENDING"} label="B4: Ресми есеп (10%)" />
        </div>
      </div>

      {/* Workspace */}
      {active && (
        <div className="arena-workspace">
          <div className="inline">
            <Crosshair size={16} />
            <strong>
              {workspaceUrl
                ? "Сіздің Kali desktop (GUI)"
                : "Орта дайындалып жатыр…"}
            </strong>
            {workspaceUrl && (
              <span className="muted small">{workspaceUrl}</span>
            )}
            {terminalUrl && (
              <a className="button ghost" style={{ padding: "4px 10px", fontSize: 11 }} href={terminalUrl} target="_blank" rel="noreferrer">
                <Terminal size={13} /> ttyd терминал
              </a>
            )}
          </div>
          {!workspaceUrl ? (
            <div className="arena-preparing">
              <Loader2 size={22} className="spin" />
              <span>Контейнерлер іске қосылуда (1–2 минут) — бет өздігінен жаңарып отырады.</span>
            </div>
          ) : frameFailed ? (
            <div className="inline" style={{ gap: 10 }}>
              <Button variant="secondary" onClick={() => setFrameFailed(false)}>
                <RefreshCw size={15} /> Қайта қосу
              </Button>
              <a className="btn btn-primary" href={workspaceUrl} target="_blank" rel="noreferrer">
                <ExternalLink size={15} /> Жаңа терезеде ашу
              </a>
            </div>
          ) : (
            <iframe
              className="arena-frame"
              src={workspaceUrl}
              title="Kali workspace"
              sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock"
              allow="fullscreen"
              onError={() => setFrameFailed(true)}
            />
          )}
        </div>
      )}

      {myRole && active && (
        <SubmitPanel match={m} role={myRole} onChanged={onChanged} />
      )}
      {isStaff && (
        <StaffPanel match={m} onChanged={onChanged} />
      )}
    </div>
  );
}

function TeamChip({
  label,
  name,
  score,
  color,
  mine,
}: {
  label: string;
  name: string;
  score: number;
  color: "red" | "blue";
  mine: boolean;
}) {
  return (
    <div className={`team-chip team-${color} ${mine ? "mine" : ""}`}>
      <span className="team-role">{label}</span>
      <span className="team-name">
        <User size={14} /> {name}
      </span>
      <span className="team-score">{score.toFixed(0)}</span>
    </div>
  );
}

function GoalRow({ ok, pend, label }: { ok: boolean; pend?: boolean; label: string }) {
  return (
    <div className={`goal-row ${ok ? "ok" : ""} ${pend ? "pend" : ""}`}>
      <span className="goal-mark">{ok ? "✓" : pend ? "…" : "○"}</span>
      <span>{label}</span>
    </div>
  );
}

/* --------------------------- Тапсыру панель --------------------------- */

function SubmitPanel({
  match: m,
  role,
  onChanged,
}: {
  match: MatchItem;
  role: "red" | "blue";
  onChanged: () => void;
}) {
  const [kind, setKind] = useState<string>(
    role === "red" ? "flag" : "detection",
  );
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();

  const kinds =
    role === "red"
      ? [
          { id: "flag", label: "G1: Flag (LIVE-де)" },
          { id: "vuln", label: "G2: Осалдылық сипаттамасы" },
          { id: "report", label: "G3: Ресми есеп (REPORTS-те)" },
        ]
      : [
          { id: "detection", label: "B3: Оқиға анықтау (Red IP)" },
          { id: "report", label: "B4: Ресми есеп (REPORTS-те)" },
        ];

  async function submit() {
    setBusy(true);
    setMsg(undefined);
    try {
      const res = await post<{ status: string; feedback: string }>(
        `/arena/matches/${m.id}/submit`,
        { kind, text },
      );
      setMsg({ ok: res.status === "PASSED", text: res.feedback });
      setText("");
      onChanged();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="arena-submit">
      <div className="inline">
        <Flag size={16} />
        <strong>Тапсыру</strong>
      </div>
      <div className="inline" style={{ flexWrap: "wrap" }}>
        {kinds.map((k) => (
          <label key={k.id} className={`chip-select ${kind === k.id ? "on" : ""}`}>
            <input
              type="radio"
              name={`kind-${m.id}`}
              checked={kind === k.id}
              onChange={() => setKind(k.id)}
            />
            {k.label}
          </label>
        ))}
      </div>
      <textarea
        className="input"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={
          kind === "flag"
            ? "flag{...} — Shop-тан алған flag"
            : kind === "detection"
              ? "Анықталған оқиға: Red-тің IP-сі + сипаттама (логтар: curl -H 'Authorization: Bearer <blue токен>' http://<target>:8080/api/admin/logs)"
              : "Ресми есеп мәтіні..."
        }
      />
      {role === "blue" && (
        <p className="muted small">
          Blue Team қолы: нысанада <code>blue_team / Blue2026</code> акаунты
          бар: <code>POST /api/login</code> → токен; түзету:
          <code> POST /api/admin/fix</code>; логтар:
          <code> GET /api/admin/logs</code>. Target IP: {m.target_ip ?? "..."}
        </p>
      )}
      <div className="inline">
        <Button onClick={submit} disabled={busy || !text.trim()}>
          {busy ? <Loader2 size={15} className="spin" /> : <Flag size={15} />}
          Тапсыру
        </Button>
        {msg && (
          <span className={msg.ok ? "feedback ok inline" : "feedback fail inline"}>
            {msg.text}
          </span>
        )}
      </div>
    </div>
  );
}

/* --------------------------- Staff панель --------------------------- */

function StaffPanel({
  match: m,
  onChanged,
}: {
  match: MatchItem;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string>();
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  async function act(path: string, body?: unknown) {
    setBusy(path);
    setMsg(undefined);
    try {
      const res = await post<{ red_score?: number; blue_score?: number }>(
        `/arena/matches/${m.id}/${path}`,
        body,
      );
      setMsg({ ok: true, text: `Дөңгеленді: Red ${res.red_score ?? "—"}, Blue ${res.blue_score ?? "—"}` });
      onChanged();
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally {
      setBusy(undefined);
    }
  }

  return (
    <div className="arena-staff">
      <div className="inline" style={{ flexWrap: "wrap", gap: 8 }}>
        <Button variant="secondary" disabled={!!busy} onClick={() => act("grade", { kind: "red_vuln", status: "PASSED" })}>
          G2 ✓
        </Button>
        <Button variant="secondary" disabled={!!busy} onClick={() => act("grade", { kind: "red_report", status: "PASSED" })}>
          G3 ✓
        </Button>
        <Button variant="secondary" disabled={!!busy} onClick={() => act("grade", { kind: "blue_report", status: "PASSED" })}>
          B4 ✓
        </Button>
        <Button variant="danger" disabled={!!busy} onClick={() => act("cancel")}>
          Матчты тоқтату
        </Button>
        {isAdmin && (m.red_workspace_url || m.blue_workspace_url) && (
          <Link
            className="button primary"
            style={{ padding: "6px 14px", fontSize: 13 }}
            href={`/arena/watch/${m.id}`}
          >
            <Eye size={15} /> Екі экран (admin)
          </Link>
        )}
      </div>
      {m.target_ip && (
        <span className="muted small">Target IP: {m.target_ip}</span>
      )}
      {msg && (
        <span className={msg.ok ? "feedback ok inline" : "feedback fail inline"}>
          {msg.text}
        </span>
      )}
    </div>
  );
}

/* ------------------------- Матч жасау модалы ------------------------- */

function CreateMatchModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const students = useApi<{ items: { id: string; name: string; email: string }[] }>(
    open ? "/arena/students" : null,
  );
  const [red, setRed] = useState("");
  const [blue, setBlue] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string>();

  useEffect(() => {
    if (open) {
      setRed("");
      setBlue("");
      setErr(undefined);
    }
  }, [open]);

  async function create() {
    setBusy(true);
    setErr(undefined);
    try {
      await post(`/arena/matches`, { redUserId: red, blueUserId: blue });
      onCreated();
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Жаңа Red/Blue матч" open={open} onClose={onClose}>
      <p className="muted">
        Екі студентті таңдаңыз: Red (шабуыл, Kali) және Blue (қорғаныс, Kali).
        Ортақ «Shop» нысана автоматты іске қосылады.
      </p>
      {students.loading && <Loader2 size={18} className="spin" />}
      {students.data && (
        <div className="stack" style={{ marginTop: 12 }}>
          <label className="field">
            <span>Red Team (шабуыл)</span>
            <select className="input" value={red} onChange={(e) => setRed(e.target.value)}>
              <option value="">— таңдаңыз —</option>
              {students.data.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.email})
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Blue Team (қорғаныс)</span>
            <select className="input" value={blue} onChange={(e) => setBlue(e.target.value)}>
              <option value="">— таңдаңыз —</option>
              {students.data.items.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.email})
                </option>
              ))}
            </select>
          </label>
          {err && (
            <div className="feedback fail inline" style={{ marginTop: 8 }}>
              {err}
            </div>
          )}
          <Button onClick={create} disabled={busy || !red || !blue || red === blue}>
            {busy ? <Loader2 size={15} className="spin" /> : <Trophy size={15} />}
            Матчты бастау
          </Button>
        </div>
      )}
    </Modal>
  );
}

/* ------------------- Admin: екі жақтың экраны (god-view) ------------------- */

export function ArenaWatchPage({ id }: { id: string }) {
  const auth = useAuth();
  const matches = useApi<{ items: MatchItem[] }>("/arena", 8_000);
  const m = matches.data?.items.find((x) => x.id === id);
  const active = m && ["PREPARING", "PREP", "LIVE", "REPORTS"].includes(m.status);

  // Арена ашылғанда бүткіл сайт қара боп кетеді
  useEffect(() => {
    document.documentElement.classList.add("site-dark");
    return () => document.documentElement.classList.remove("site-dark");
  }, []);

  const frame = (url: string | undefined, title: string, color: string, label: string) => (
    <div className="watch-pane" data-side={label}>
      <div className="watch-head" style={{ borderColor: color }}>
        <span className="watch-dot" style={{ background: color }} />
        <strong>{title}</strong>
        <span className="muted small">{m?.mission ?? ""}</span>
        {url && (
          <a href={url} target="_blank" rel="noreferrer" style={{ marginLeft: "auto" }}>
            <ExternalLink size={14} />
          </a>
        )}
      </div>
      {active && url ? (
        <iframe
          className="watch-frame"
          src={url}
          title={title}
          sandbox="allow-scripts allow-same-origin allow-forms allow-pointer-lock"
          allow="fullscreen"
        />
      ) : (
        <div className="watch-empty">
          {active ? "Экран әзірленбейді (орта дайындалуда…)" : "Матч белсенді емес"}
        </div>
      )}
    </div>
  );

  return (
    <div className="page arena-dark">
      <RequireAuth roles={["ADMIN"]}>
        <div className="inline" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
          <div className="inline">
            <Eye size={18} />
            <h1 style={{ margin: 0, fontSize: 18 }}>Admin: екі экран (god-view)</h1>
            {m && <Badge value={m.status} />}
          </div>
          <Link className="button ghost" style={{ padding: "6px 12px", fontSize: 13 }} href="/arena">
            ← Аренаға қайту
          </Link>
        </div>
        {!m && <Empty title="Матч табылмады" text="ID тексеріңіз немесе арена бетіне қайтып оралыңыз." />}
        {m && (
          <>
            <div className="arena-teams" style={{ marginBottom: 12 }}>
              <TeamChip label="RED" name={m.red_name} score={m.red_score} color="red" mine={false} />
              <span className="arena-vs">VS</span>
              <TeamChip label="BLUE" name={m.blue_name} score={m.blue_score} color="blue" mine={false} />
            </div>
            <div className="watch-grid">
              {frame(m.red_workspace_url, `RED — ${m.red_name}`, "#e5484d", "red")}
              {frame(m.blue_workspace_url, `BLUE — ${m.blue_name}`, "#3b82f6", "blue")}
            </div>
          </>
        )}
      </RequireAuth>
    </div>
  );
}
